<template>
  <div>
    <VmsPageHeader
      kicker="Directory"
      title="Facilities"
      icon="mdi-bank"
    >
      <template #actions>
        <v-select
          v-model="selectedHeaders"
          :items="headers"
          label="Column Visibility"
          multiple
          outlined
          return-object
          dense
          hide-details
          @change="toggleColumnVisibility"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2">
              <span>{{ item.text }}</span>
            </v-chip>
            <span v-if="index === 2" class="grey--text caption">(+{{ selectedHeaders.length - 2 }} others)</span>
          </template>
        </v-select>

        <v-btn color="green" dark class="text-capitalize" elevation="0" @click="createFacilityModal">
          <v-icon left>mdi-bank-plus</v-icon>
          <span>Add Facility</span>
        </v-btn>
      </template>
    </VmsPageHeader>

    <VmsResponsiveList :items="facilitiesWithIndex" :loading="loading" loading-text="Loading facilities…">
      <template #table>
        <v-data-table
          :headers="computedHeaders"
          :items="facilitiesWithIndex"
          class="elevation-0 vms-surface"
          :loading="loading"
          loading-text="Loading... Please wait"
          :search="search"
        >
          <template v-slot:[`item.facility`]="{ item }">
            <div class="py-1">
              <a
                @click.prevent="selectFacility(item, 4)"
                class="info--text text-decoration-none font-weight-medium"
                href="#"
              >{{ item.attributes?.facility }}</a>
              <div
                v-if="item.relationships.parent_facility != null"
                class="caption grey--text text--darken-1"
              >
                {{ item.relationships.parent_facility?.attributes?.facility }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.facility_manager`]="{ item }">
            <div class="py-1">
              <div>
                {{ item.relationships.manager?.attributes.legal_name }}
                <template v-if="item.relationships.manager?.attributes.parent_name != null">
                  — {{ item.relationships.manager?.attributes.parent_name }}
                </template>
              </div>
              <div
                v-if="item.relationships.manager?.relationships.admin != null"
                class="caption info--text"
              >
                {{ item.relationships.manager?.relationships.admin.attributes.name }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.allEntries`]="{ item }">
            <div v-if="(item.relationships.entries || []).length" class="py-1">
              <div
                v-for="(entry, index) in item.relationships.entries"
                :key="`facility-dash-entry-${item.id}-${entry.id || entry?.attributes?.id || index}`"
                class="d-flex align-center flex-wrap mb-1"
                style="gap: 4px;"
              >
                <span class="caption">
                  {{ index + 1 }}. {{ entry.attributes.passage }}
                  <span class="grey--text">— {{ entry.relationships.type?.attributes?.type }}</span>
                </span>
                <v-btn
                  icon
                  x-small
                  color="primary"
                  class="ml-1"
                  @click="editFacilityEntry(item, entry)"
                >
                  <v-icon x-small>mdi-pencil-outline</v-icon>
                </v-btn>
                <span
                  v-if="entry.relationships.security_level != null"
                  class="caption info--text"
                >
                  L{{ entry.relationships.security_level.attributes.level }}
                </span>
              </div>
            </div>
            <span v-else class="grey--text caption">None</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="vms-table-actions">
              <VmsIconAction icon="mdi-location-enter" label="Add entry point" @click="addEntryPoint(item)" />
              <VmsIconAction icon="mdi-dots-horizontal" label="Open" @click="selectFacility(item, 1)" />
              <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editFacility(item)" />
              <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="openDeleteDialog(item)" />
            </div>
          </template>

          <template v-slot:no-data>
            <a class="info--text text--darken-1">No Facilities</a>
          </template>
        </v-data-table>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">
          <span @click="selectFacility(item, 4)" class="row-pointer info--text" role="button">{{ item.attributes?.facility }}</span>
        </div>
        <div class="vms-list-card__meta" v-if="item.relationships.parent_facility != null">
          <span @click="selectFacility(item, 4)" class="row-pointer info--text" role="button">Parent: {{ item.relationships.parent_facility?.attributes?.facility }}</span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">Manager</span>
          <span>
            {{ item.relationships.manager?.attributes.legal_name }}
            <template v-if="item.relationships.manager?.relationships.admin != null">
              - {{ item.relationships.manager?.relationships.admin.attributes.name }}
            </template>
          </span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">Passages</span>
          <span>{{ (item.relationships.entries || []).length }}</span>
        </div>
        <div class="vms-list-card__actions">
          <VmsIconAction icon="mdi-dots-horizontal" label="Open" @click="selectFacility(item, 1)" />
          <VmsIconAction icon="mdi-location-enter" label="Add entry point" @click="addEntryPoint(item)" />
          <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editFacility(item)" />
          <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="openDeleteDialog(item)" />
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <v-icon large color="grey lighten-1">mdi-bank</v-icon>
          <p class="mt-2 grey--text px-4">No Facilities</p>
        </div>
      </template>
    </VmsResponsiveList>

    <div class="text-center">
      <v-dialog v-model="showDeleteDialog" persistent width="400">
        <v-card>
          <v-card-title class="headline">
            Proceed with caution!
            <span class="float-right">
              <v-icon class="mdi mdi-alert-circle-outline"></v-icon>
            </span>
          </v-card-title>
          <v-card-text>
            You are about to delete a facility,
            are you sure you want to delete this item?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" text @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" text @click="deleteFacility(facilityToDelete)">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <v-row justify="center">
      <v-dialog v-model="showFacilityModal" scrollable max-width="600">
        <v-card>
          <v-card-title class="grey--text text-h5">
            Facility
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="mb-0 pb-0">
            <v-form class="pt-7 pb-0" ref="form" v-model="valid" lazy-validation>
              <v-autocomplete 
                dense v-model="facility.org_id" :items="facility_manager_organizations" item-value="id"
                item-text="attributes.legal_name" label="Organization" :error-messages="apiErrors.org_id"
                outlined readonly :rules="rules.select"
              ></v-autocomplete>

              <v-autocomplete 
                dense 
                v-model="facility.usr_id" 
                :items="organizationUsers" 
                item-value="id"
                item-text="attributes.name" 
                label="Facility Manager" 
                :error-messages="apiErrors.usr_id" 
                outlined
                :rules="rules.select"
              ></v-autocomplete>

              <v-text-field 
                dense v-model="facility.facility" label="Facility" :error-messages="apiErrors.facility"
                outlined
                :rules="rules.required"
              ></v-text-field>

              <gmap-map ref="map" :zoom="zoom" :center="center" style="max-width:100%; height:400px;"
                @click="handleMapClick"
                v-if="viewGoogleMap"
              >
                <GmapMarker 
                  :position="centerPosition" :clickable="true" :draggable="true" @drag="handleMarkerDrag"
                  @click="panToMarker" 
                  />
              </gmap-map>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-actions>
            <v-btn color="red darken-1" text @click="closeModal" class="text-capitalize mx-1">
              Cancel
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn v-if="editFacilityModal" color="green darken-1 white--text" @click="saveFacilityUpdates" class="text-capitalize mx-1" :loading="submitting">
              Update
            </v-btn>

            <v-btn v-else color="green darken-1 white--text" @click="saveFacility" class="text-capitalize mx-1" :loading="submitting">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showEntryPointModal" scrollable max-width="600">
        <v-card>
          <v-card-title class="grey--text text-h5">
            Passage
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="mb-0 pb-0">
            <v-form class="pt-7 pb-0" ref="form1" v-model="valid" lazy-validation>
              <v-autocomplete 
                dense 
                v-model="facility.id_parent" 
                :items="allFacilities" 
                item-value="id"
                item-text="attributes.facility" 
                label="Facility" 
                :error-messages="apiErrors.id_parent" 
                outlined
                @change="childFacility = true"
                :rules="rules.select"
              ></v-autocomplete>

              <v-row>
                <v-col>
                  <v-text-field 
                    dense 
                    v-model="passage.entry_point_name" 
                    label="Name"
                    :error-messages="apiErrors.entry_point_name" 
                    outlined
                    :rules="rules.required"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-select 
                    dense 
                    v-model="passage.passage_type_id" 
                    :items="passage_types" 
                    item-value="id"
                    item-text="attributes.type" 
                    label="Type" 
                    :error-messages="apiErrors.passage_type_id"
                    outlined
                    :rules="rules.select"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-select 
                    dense 
                    v-model="passage.security_level_id" 
                    :items="security_levels" 
                    item-value="id"
                    item-text="attributes.level" 
                    label="Security Level" 
                    :error-messages="apiErrors.security_level_id"
                    outlined
                    :rules="rules.select"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-select 
                    dense 
                    v-model="passage.security_level_id" 
                    :items="security_levels" 
                    item-value="id"
                    item-text="attributes.description" 
                    label="Security Level"
                    :error-messages="apiErrors.security_level_id" 
                    outlined 
                    readonly
                  ></v-select>
                </v-col>
              </v-row>

              <v-textarea 
                dense 
                v-model="passage.description" 
                label="Description"
                :error-messages="apiErrors.description" 
                outlined
              ></v-textarea>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn color="red darken-1" text @click="closeModal" class="text-capitalize mx-1">
              Cancel
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn v-if="!editFacilityPassage" color="green darken-1 white--text" @click="saveFacilityEntryPoint" class="text-capitalize mx-1" :loading="submitting">
              Save
            </v-btn>
            <v-btn v-if="editFacilityPassage" color="green darken-1 white--text" @click="updateFacilityEntryPoint" class="text-capitalize mx-1" :loading="submitting">
              Update
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import FacilitiesService from '../../services/facilities.service';
import EntriesService from '../../services/entries.service';
import PassageTypeService from '../../services/passage_type.service';
import SecurityLevelService from '../../services/security_level.service';
import Facility from '../../models/facility';
import Passage from '../../models/passage';
import OrganizationService from '../../services/organization.service';
import VmsPageHeader from '../../components/VmsPageHeader.vue';
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';

export default {
  name: "Facility",
  components: { VmsPageHeader, VmsResponsiveList, VmsIconAction },
  props: [
    "parentFacility"
  ],
  data() {
    return {
      facilityToDelete: null,
      search: '',
      facilities: [],
      allFacilities: [],
      childFacility: false,
      passage_types: [],
      security_levels: [],
      loading: true,
      showDeleteDialog: false,
      selectedHeaders: [],
      valid: false,
      rules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 3) || "Min 3 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
      },
      headers: [
        {
          text: "#",
          align: "start",
          filterable: true,
          value: "index",
          visible: true,
          width: "56px",
        },
        {
          text: "Facility",
          align: "start",
          filterable: true,
          value: "facility",
          visible: true,
        },
        {
          text: "Manager",
          align: "start",
          filterable: true,
          value: "facility_manager",
          visible: false,
        },
        {
          text: "Passages",
          align: "start",
          filterable: false,
          value: "allEntries",
          visible: true,
        },
        {
          text: "Actions",
          align: "end",
          filterable: false,
          value: "actions",
          sortable: false,
          visible: true,
        },
      ],
      submitting: false,
      viewGoogleMap: false,
      showFacilityModal: false,
      editFacilityPassage: false,
      facility: new Facility(),
      passage: new Passage(),
      apiErrors: [],
      editFacilityModal: false,
      user_managed_organizations: [],
      facility_manager_organizations: [],
      showEntryPointModal: false,
      zoom: 6,
      map: null,
      center: { lat: -1.262223346227177, lng: 36.80714185645724 },
      centerPosition: { lat: -1.262223346227177, lng: 36.80714185645724 },
      selectedFacility: null,
      organizationUsers: [],
      selectedTabId: null,
      editedItem: {},
    }
  },
  computed: {
    facilitiesWithIndex() {
      return this.facilities.map(
        (items, index) => ({
          ...items,
          index: index + 1,
        })
      )
    },
    user() {
      return JSON.parse(localStorage.getItem("user"));
    },
    org() {
      return JSON.parse(localStorage.getItem("org"));
    },
    computedHeaders() {
      const headers = this.headers
        .filter((item) => item.visible === true || item.value === "actions")
        .map((item) =>
          item.value === "actions" ? { ...item, visible: true } : item
        );
      return headers;
    },
  },
  created() {
    this.$nextTick(() => {
      this.syncSelectedHeaders();
      this.getFacilities();
      this.getUserManagedOrganizations();
      this.getAllFacilities();
      this.getFacilityManagerOrganizations();
      this.geolocate();
      this.getUsers();
    });
  },
  methods: {
    syncSelectedHeaders() {
      this.headers.forEach((h) => {
        if (h.value === "actions") h.visible = true;
      });
      this.selectedHeaders = this.headers.filter(
        (h) => h.visible === true || h.value === "actions"
      );
    },

    openDeleteDialog(item) {
      this.facilityToDelete = item;
      this.showDeleteDialog = true;
    },
    toggleColumnVisibility() {
      if (this.selectedHeaders && this.selectedHeaders.length > 0) {
        this.headers.forEach((facilityHeader) => {
          const index = this.headers.findIndex(
            (header) => header.value == facilityHeader.value
          );
          if (index > -1) {
            this.headers[index].visible = this.headers[index].value === "actions"
              ? true
              : false;
          }
        });

        this.selectedHeaders.forEach((selectedHeader) => {
          const index = this.headers.findIndex(
            (header) => header.value == selectedHeader.value
          );
          if (index > -1) {
            this.headers[index].visible = true;
          }
        });

        const actionsHeader = this.headers.find((h) => h.value === "actions");
        if (
          actionsHeader &&
          !this.selectedHeaders.some((h) => h.value === "actions")
        ) {
          this.selectedHeaders = [...this.selectedHeaders, actionsHeader];
        }
      }
    },
    backToFacilityList() {
      this.selectedFacility = null;
    },
    // Fetch organization users
    getUsers() {
      OrganizationService.all_organization_users().then(
        success => {
          this.organizationUsers = success.data ? success.data.data : this.organizationUsers;
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
        }
      )
    },
    getFacilities() {
      let orgId = this.org?.id;
      let facilityId = this.selectedFacility?.id ?? this.parentFacility?.id;
      this.loading = true;
      
      FacilitiesService.index(orgId, facilityId).then(
        (response) => {
          if (response.status == 200) {
            this.facilities = response.data.data;
          }

          this.loading = false;
        },
        (error) => {
          this.loading = false;
          
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    getAllFacilities() {
      FacilitiesService.allFacilities(this.org?.id).then(
        (response) => {
          if (response.status == 200) {
            this.allFacilities = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    selectFacilityOrganizations(facility) {
      this.selectedFacility = facility;
    },
    getFacilityManagerList() {
      FacilitiesService.facilityManagerList(this.org?.id).then(
        (response) => {
          if (response.status == 200) {
            this.facilities = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    getFacilityManagerOrganizations() {
      OrganizationService.facilityManagerOrganizations(this.user.id).then(
        (response) => {
          if (response.status == 200) {
            this.facility_manager_organizations = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    getPassageTypes() {
      PassageTypeService.index().then(
        (response) => {
          if (response.status == 200) {
            this.passage_types = response.data.data;
          }
        },
        (error) => {
          console.log(error)
        }
      );
    },
    getSecurityLevels() {
      SecurityLevelService.index().then(
        (response) => {
          if (response.status == 200) {
            this.security_levels = response.data.data;
          }
        },
        (error) => {
          console.log(error)
        }
      );
    },
    getUserManagedOrganizations() {
      OrganizationService.user_created_organizations().then(
        (response) => {
          this.user_managed_organizations = response.data.data
        },
        (error) => {
          console.log(error)
        }
      );
    },
    createFacilityModal() {
      this.facility.id_parent = this.parentFacility.id;
      this.facility.usr_id = this.parentFacility?.attributes?.facility_manager_id;

      let facility_manager_organization = this.parentFacility?.relationships.manager;
      if (facility_manager_organization != null) {
        this.facility.org_id = facility_manager_organization.id;
      }

      this.showFacilityModal = true;
      this.getFacilityManagerOrganizations();
    },
    closeModal() {
      this.editFacilityPassage = false;
      this.showFacilityModal = false;
      this.showDeleteDialog = false;
      this.facility = new Facility();
      this.passage = new Passage();
      this.apiErrors = [];

      this.editFacilityModal = false;

      this.showEntryPointModal = false;
      this.geolocate();
    },
    saveFacility() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        // child facilities use lat and long of the parent facility
        if (this.facility.id_parent) {
          this.facility.latitude = null;
          this.facility.longitude = null;
        } else {
          this.facility.parent_id = null;
        }
  
        this.facility.org_id = this.org?.id;
        this.submitting = true;

        FacilitiesService.store(this.facility).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", "Facility created", response.data.message);
              this.getFacilities();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", "Facility not created", response.data.message);
              this.loading = false;
            }

            this.submitting = false;
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.getFacilities();
            }
  
            if (error.response.status == 401) {
              this.handleLogout();
            }
            
            this.$store.dispatch("alert/error", error.response.data.message);
            this.submitting = false;
          }
        );
      }
    },
    editFacilityEntry(facility, passage) {
      this.editFacilityPassage = true;
      this.passage.id = passage.id;
      this.passage.fcl_id = facility.id;
      this.passage.entry_point_name = passage.attributes.passage;
      this.passage.description = passage.attributes.description;
      let relationships = passage.relationships;

      if (relationships.type != undefined) {
        this.passage.passage_type_id = passage.relationships.type.id;
      }

      if (relationships.security_level != undefined) {
        this.passage.security_level_id = passage.relationships.security_level.id;
      }

      this.addEntryPoint(facility)
    },
    editFacility(facility) {
      this.facility.id = facility.id;
      let attributes = facility.attributes;
      this.facility.facility = attributes.facility;
      this.facility.latitude = attributes.latitude;
      this.facility.longitude = attributes.longitude;
      this.facility.usr_id = attributes.facility_manager_id?.toString();

      this.getFacilityManagerOrganizations();

      if (attributes.latitude && attributes.longitude) {
        this.setFacilityGeoLocation(attributes)
      }

      if (facility.relationships.parent_facility != null) {
        this.facility.id_parent = facility.relationships.parent_facility.id;
        this.setFacilityGeoLocation(facility.relationships.parent_facility.attributes);
      }

      let facility_manager_organization = facility.relationships.manager;
      if (facility_manager_organization != null) {
        this.facility.org_id = facility_manager_organization.id;
      }

      this.showFacilityModal = true;
      this.editFacilityModal = true;
    },
    // deleting the data from the database
    deleteFacility(item) {
      FacilitiesService.delete(item).then(
        (response) => {
          if (response.status === 200) {
            let index = this.items.indexOf(item);
            this.items.splice(index, 1)
            this.$store.dispatch("alert/success", "Deleted successfully", response.data.message);
            this.closeModal();
            this.getFacilities();
          } else {
            this.$store.dispatch("alert/error", "Something went wrong", response.data.message);
          }
        },
        (error) => {
          if (error.response.status === 422) {
            this.$store.dispatch("alert/error", error.response.data.message);
          }

          if (error.response.status === 401) {
            this.handleLogout();
          }

          this.$store.dispatch("alert/error", error.response.data.message);
        }
      );
    },
    setFacilityGeoLocation(facility) {
      if (facility.latitude && facility.longitude) {
        this.centerPosition.lat = facility.latitude;
        this.centerPosition.lng = facility.longitude;
        this.zoom = 18
      }
    },
    saveFacilityUpdates() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.submitting = true;

        FacilitiesService.update(this.facility).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", "Facility edited successfully", response.data.message);
              this.loading = false;
              this.getFacilities();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.getFacilities();
              this.loading = false;
            }

            this.submitting = false;
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.getFacilities();
            }

            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.getFacilities();
            }
            
            this.$store.dispatch("alert/error", error.response.data.message);
            this.submitting = false;
          }
        );
      }
    },
    addEntryPoint(item) {
      this.facility.fcl_id = item.id;
      this.facility.id_parent = item.id;
      this.showEntryPointModal = true;
      this.getPassageTypes();
      this.getFacilities();
      this.getSecurityLevels();
    },
    saveFacilityEntryPoint() {
      this.valid = this.$refs.form1.validate();
      if (this.valid) {
        this.submitting = true;
        this.passage.fcl_id = this.facility.id_parent;

        EntriesService.store(this.passage).then(
          (response) => {
            if (response.data.code == 200) {
              this.$store.dispatch("alert/success", "Added passage successfully", response.data.message);
              this.loading = false;
              this.getFacilities();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.getFacilities();
              this.loading = false;
            }

            this.submitting = false;
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.getFacilities();
            }

            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.getFacilities();
            }

            this.submitting = false;
            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      }
    },
    updateFacilityEntryPoint() {
      this.valid = this.$refs.form1.validate();
      if (this.valid) {
        this.submitting = true;
        this.passage.fcl_id = this.facility.id_parent;

        EntriesService.update(this.passage).then(
          (response) => {
            if (response.data.code == 200) {
              this.$store.dispatch("alert/success", "Update passage successfully", response.data.message);
              this.loading = false;
              this.getFacilities();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.getFacilities();
              this.loading = false;
            }

            this.submitting = false;
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.getFacilities();
            }

            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.getFacilities();
            }
            
            this.submitting = false;
            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      }
    },
    geolocate() {
      navigator.geolocation.getCurrentPosition((position) => {
        this.centerPosition.lat = position.coords.latitude;
        this.centerPosition.lng = position.coords.longitude;
        this.center = this.centerPosition;
        this.zoom = 18;
      });
    },
    // sets the position of marker when dragged
    handleMarkerDrag(e) {
      this.centerPosition = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      this.panToMarker();
    },
    // Moves the map view port to marker
    panToMarker() {
      this.facility.latitude = this.centerPosition.lat ?? this.center.lat;
      this.facility.longitude = this.centerPosition.lng ?? this.center.log;

      this.$refs.map.panTo(this.centerPosition);
      this.zoom = 18;
    },
    // Moves the marker to click position on the map
    handleMapClick(e) {
      this.centerPosition = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      this.panToMarker();
    },
    selectFacility(facility, tabId = 1) {
      this.$emit('select_facility', { selectedTabId: tabId, selectedFacility: facility });
    },
  },
  watch: {
    parentFacility: {
      handler (val) {
        this.selectedFacility = val;
        this.getFacilities();
      },
      deep: true,
    },
  },
} 
</script>